import { Loader2, Search } from 'lucide-react';
import { useMemo, useState, type UIEvent } from 'react';

import { useActivitySearchInfinite } from '../api/use-activity-catalog';
import type { ActivityDiscoveryCandidate } from '../types';

import { SearchFilterToolbar } from './SearchFilterToolbar';

import { Alert, AlertDescription } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Select } from '@/components/ui/select';
import { useDebounce } from '@/hooks/use-debounce';
import { useTranslation } from 'react-i18next';
interface Scope3ActivityPickerProps {
  scope: 1 | 2 | 3;
  activityCategory: string;
  scope3Category?: number;
  selectedMethod: string;
  disabled?: boolean;
  onUseActivity: (candidate: ActivityDiscoveryCandidate) => void;
}

function asPrimitiveString(value: unknown): string | undefined {
  if (typeof value === 'string') return value;
  if (typeof value === 'number') return String(value);
  return undefined;
}

function toSelectOptions(values: unknown, fallbackLabel: string): { value: string; label: string }[] {
  if (!Array.isArray(values) || values.length === 0) {
    return [{ value: '', label: fallbackLabel }];
  }

  const seen = new Set<string>();
  const options = values
    .map((item) => {
      if (typeof item === 'string') {
        return { value: item, label: item };
      }
      if (typeof item !== 'object' || item === null) return undefined;
      const objectItem = item as Record<string, unknown>;
      const value =
        asPrimitiveString(objectItem.id) ??
        asPrimitiveString(objectItem.value) ??
        asPrimitiveString(objectItem.code) ??
        '';
      const label =
        asPrimitiveString(objectItem.label) ??
        asPrimitiveString(objectItem.name) ??
        asPrimitiveString(objectItem.title) ??
        value;
      return value ? { value, label } : undefined;
    })
    .filter((item): item is { value: string; label: string } => Boolean(item))
    .filter((item) => {
      if (seen.has(item.value)) return false;
      seen.add(item.value);
      return true;
    });

  if (options.length === 0) return [{ value: '', label: fallbackLabel }];
  return [{ value: '', label: fallbackLabel }, ...options];
}

export function Scope3ActivityPicker({
  scope,
  activityCategory,
  scope3Category,
  selectedMethod,
  disabled,
  onUseActivity,
}: Scope3ActivityPickerProps) {
  const { t } = useTranslation('ghg');
  const [isOpen, setIsOpen] = useState(true);
  const [query, setQuery] = useState('');
  const [source, setSource] = useState('');
  const [region, setRegion] = useState('');
  const [yearInput, setYearInput] = useState('');

  const debouncedQuery = useDebounce(query, 500);
  const normalizedYear = yearInput.trim() ? Number(yearInput) : undefined;
  const isScope3 = scope === 3;

  const searchQuery = useActivitySearchInfinite(
    {
      scope,
      activityCategory,
      ...(typeof scope3Category === 'number' ? { scope3Category } : {}),
      unitType: selectedMethod,
      ...(debouncedQuery.trim() ? { query: debouncedQuery.trim() } : {}),
      ...(source.trim() ? { source: source.trim() } : {}),
      ...(region.trim() ? { region: region.trim() } : {}),
      ...(typeof normalizedYear === 'number' && Number.isFinite(normalizedYear)
        ? { year: normalizedYear }
        : {}),
      pageSize: 50,
    },
    {
      enabled:
        isOpen &&
        Boolean(activityCategory) &&
        Boolean(selectedMethod) &&
        (!isScope3 ||
          (typeof scope3Category === 'number' &&
            Number.isFinite(scope3Category) &&
            scope3Category > 0)),
    }
  );

  const items = useMemo(() => {
    const deduped: ActivityDiscoveryCandidate[] = [];
    const seen = new Set<string>();
    for (const item of searchQuery.items) {
      const dedupeKey = [
        item.activityId,
        item.defaultUnit,
        item.source,
        item.region,
        String(item.year),
        item.unitType,
      ].join('|');
      if (seen.has(dedupeKey)) continue;
      seen.add(dedupeKey);
      deduped.push(item);
    }
    return deduped;
  }, [searchQuery.items]);
  const hasMore = searchQuery.hasMore;
  const isLoading = searchQuery.isLoading;
  const isError = searchQuery.isError;
  const isFetchingMore = searchQuery.isFetchingNextPage;
  const possibleFilters = searchQuery.possibleFilters;

  const sourceOptions = useMemo(
    () => toSelectOptions(possibleFilters?.source, 'Any source'),
    [possibleFilters?.source]
  );
  const regionOptions = useMemo(
    () => toSelectOptions(possibleFilters?.region, 'Any region'),
    [possibleFilters?.region]
  );

  const handleResultsScroll = (event: UIEvent<HTMLDivElement>) => {
    if (!hasMore || isFetchingMore) return;
    const element = event.currentTarget;
    const distanceToBottom = element.scrollHeight - element.scrollTop - element.clientHeight;
    if (distanceToBottom > 80) return;
    void searchQuery.fetchNextPage();
  };

  return (
    <div className="space-y-3 rounded-md border bg-muted/10 p-3">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="space-y-1">
          <p className="text-sm font-medium">Activity Search</p>
          <p className="text-xs text-muted-foreground">
            {isScope3 ? (
              <>
                Locked to scope{' '}
                <span className="font-mono">
                  3.{typeof scope3Category === 'number' ? String(scope3Category) : 'N/A'}
                </span>{' '}
                and unit type <span className="font-mono">{selectedMethod || 'N/A'}</span>.
                Results include Climatiq and organization custom activities.
              </>
            ) : (
              <>
                Locked to scope <span className="font-mono">{String(scope)}</span> and unit type{' '}
                <span className="font-mono">{selectedMethod || 'N/A'}</span>. Results include
                Climatiq and organization custom activities.
              </>
            )}
          </p>
        </div>
        <Button
          type="button"
          size="sm"
          variant="outline"
          onClick={() => {
            setIsOpen((current) => !current);
          }}
          disabled={disabled ?? false}
        >
          {isOpen ? 'Hide search' : 'Search activities'}
        </Button>
      </div>

      {!isOpen ? null : (
        <>
          <SearchFilterToolbar
            query={query}
            onQueryChange={setQuery}
            queryPlaceholder="Search activities"
            queryDisabled={(disabled ?? false) || isFetchingMore}
            querySectionClassName="flex flex-wrap items-end gap-2"
            queryContainerClassName="min-w-80 flex-1"
            filtersPlacement="inline"
          >
            <Select
              options={sourceOptions}
              value={source}
              onChange={setSource}
              className="w-44"
              placeholder={t('placeholder.source1')}
              disabled={(disabled ?? false) || isFetchingMore}
            />
            <Select
              options={regionOptions}
              value={region}
              onChange={setRegion}
              className="w-44"
              placeholder={t('placeholder.region1')}
              disabled={(disabled ?? false) || isFetchingMore}
            />
            <Input
              type="number"
              value={yearInput}
              onChange={(event) => {
                setYearInput(event.target.value);
              }}
              className="w-28"
              placeholder={t('placeholder.year2')}
              disabled={(disabled ?? false) || isFetchingMore}
            />
          </SearchFilterToolbar>

          {!selectedMethod && (
            <Alert>
              <AlertDescription>Select a unit type first to search activities.</AlertDescription>
            </Alert>
          )}

          {isLoading && (
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading activities...
            </div>
          )}

          {isError && (
            <Alert variant="destructive">
              <AlertDescription>Activity search failed. Try different filters.</AlertDescription>
            </Alert>
          )}

          {!isLoading && !isError && items.length === 0 && (
            <Alert>
              <AlertDescription>No activities found for the selected filters.</AlertDescription>
            </Alert>
          )}

          {items.length > 0 && (
            <div className="space-y-2">
              <div className="max-h-80 space-y-2 overflow-y-auto pr-1" onScroll={handleResultsScroll}>
                {items.map((item) => {
                  const activityKey = [
                    item.activityId,
                    item.defaultUnit,
                    item.source,
                    item.region,
                    String(item.year),
                    item.unitType,
                  ].join('|');
                  return (
                    <div key={activityKey} className="rounded-md border bg-background p-3">
                      <div className="flex flex-wrap items-start justify-between gap-2">
                        <div className="space-y-1">
                          <p className="text-sm font-medium">{item.displayName}</p>
                          <p className="font-mono text-xs text-muted-foreground">{item.activityId}</p>
                          <p className="text-xs text-muted-foreground">
                            {item.source} · {item.regionName} · {String(item.year)} · {item.defaultUnit}
                          </p>
                        </div>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          disabled={disabled ?? false}
                          onClick={() => {
                            onUseActivity(item);
                          }}
                        >
                          Use activity
                        </Button>
                      </div>
                    </div>
                  );
                })}
              </div>

              {hasMore && (
                <Button
                  type="button"
                  variant="outline"
                  disabled={(disabled ?? false) || isFetchingMore}
                  onClick={() => {
                    void searchQuery.fetchNextPage();
                  }}
                >
                  {isFetchingMore ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Search className="mr-2 h-4 w-4" />
                  )}
                  Load more
                </Button>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
}
